/**
 * Pitch Detection API
 *
 * Provides a unified interface for pitch detection with support for
 * multiple underlying implementations.
 *
 * Current implementation: bluesharp-pitch-detection (YIN algorithm)
 * See PITCH_DETECTION_OPTIONS.md for alternatives and comparison.
 */

// Export types
export type {
  PitchDetectionResult,
  PitchDetectorConfig,
  PitchDetector,
  InstrumentPreset,
  RawNote,
  NoteWithAccidental,
  NoteName
} from "./types";

export { INSTRUMENT_PRESETS, noteNameToMidi, pitchResultToMidi } from "./types";

// Export utilities
export {
  frequencyToMidi,
  midiToFrequency,
  frequencyToNoteName,
  getCentsOffPitch,
  analyzeFrequency,
  isNearNote,
} from "./noteUtils";

// Export notation utilities (key signatures, accidentals, spelling)
export {
  getKeySignatureAlterations,
  shouldPreferFlat,
  midiToNoteName,
  getRequiredAccidental,
  isValidKeySignature,
  getSupportedKeySignatures,
} from "./notationUtils";

// Export transposition utilities
export {
  noteToMidi,
  midiToNote,
  transpose,
  transposeNotes,
  isInFluteRange,
  clampToFluteRange,
  getKeyName,
  getFluteRange,
} from "./transposition";

// Export sequence matching utilities
export {
  SequencePitchMatcher,
  type SequenceMatcherConfig,
  type SequenceMatcherState,
  type SequenceMatchEvent,
} from "./sequenceMatcher";

// Export detector implementations
export { BluesharpDetector } from "./detectors/BluesharpDetector";

// Re-export for convenience
import type { PitchDetector, InstrumentPreset } from "./types";
import { INSTRUMENT_PRESETS } from "./types";
import { BluesharpDetector } from "./detectors/BluesharpDetector";

/**
 * Detector type identifiers
 */
export enum DetectorType {
  BLUESHARP = "bluesharp",
  // Future options:
  // PITCHY = "pitchy",
  // ML5_CREPE = "ml5-crepe",
}

/**
 * Factory function to create a pitch detector
 *
 * @param type Detector type to create (default: BLUESHARP)
 * @param preset Optional instrument preset to apply
 * @returns Configured pitch detector instance
 *
 * @example
 * ```typescript
 * // Create default detector for flute
 * const detector = createPitchDetector(DetectorType.BLUESHARP, 'flute');
 *
 * // Use it to detect pitch
 * const result = detector.detectPitch(audioData, sampleRate);
 * if (result.frequency) {
 *   console.log(`Detected: ${result.note} at ${result.frequency}Hz`);
 *   console.log(`Off by ${result.centsOff} cents`);
 * }
 * ```
 */
export function createPitchDetector(
  type: DetectorType = DetectorType.BLUESHARP,
  preset?: InstrumentPreset
): PitchDetector {
  let detector: PitchDetector;

  switch (type) {
    case DetectorType.BLUESHARP:
      detector = new BluesharpDetector();
      break;
    // Future implementations:
    // case DetectorType.PITCHY:
    //   detector = new PitchyDetector();
    //   break;
    default:
      throw new Error(`Unknown detector type: ${type}`);
  }

  // Apply instrument preset if specified
  if (preset && preset in INSTRUMENT_PRESETS) {
    const config = INSTRUMENT_PRESETS[preset];
    detector.setFrequencyRange(config.minFrequency, config.maxFrequency);
    detector.setConfidenceThreshold(config.confidenceThreshold);
  }

  return detector;
}

/**
 * Default detector instance for flute
 * Pre-configured and ready to use
 */
export const defaultFluteDetector = createPitchDetector(
  DetectorType.BLUESHARP,
  "flute"
);
